
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Phone, CheckCircle, Clock, RefreshCw, User, Eye, AlertCircle, TrendingUp, Search } from "lucide-react";
import PartnerDashboardHeader from "@/components/partners/PartnerDashboardHeader";
import { LeadStageType, ServiceType } from "@/context/LeadContext";
import { format } from "date-fns";
import { el } from "date-fns/locale";
import LeadStatusReminderBadge from "@/components/ui-custom/LeadStatusReminderBadge";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

interface Partner {
  id: string;
  name: string;
  phone: string;
  email: string;
  location: string;
}

interface Lead {
  id: string;
  name: string;
  phone: string;
  address: string;
  postalCode: string;
  service: ServiceType;
  timeframe: string;
  stage: LeadStageType;
  createdAt: string;
  viewed: boolean;
}

const PartnerDashboard = () => {
  const { partnerId } = useParams<{ partnerId: string }>();
  const [partner, setPartner] = useState<Partner | null>(null);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    pending: 0,
    new: 0,
    viewed: 0,
    successRate: 0
  });
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("recent");
  const [searchTerm, setSearchTerm] = useState("");
  const [stageFilter, setStageFilter] = useState<LeadStageType | "all">("all");
  const [autoRefresh, setAutoRefresh] = useState(true);

  const calculateLeadScore = (lead: Lead) => {
    let score = 50;

    if (lead.timeframe === "άμεσα") score += 25;
    if (lead.timeframe === "εντός3μηνών") score += 12;
    if (lead.comments && lead.comments.trim().length > 15) score += 8;
    if (lead.viewed) score += 10;
    if (lead.stage === "Νέο") score += 10;
    if (lead.stage === "σεΕκκρεμότητα") score -= 5;

    return Math.max(0, Math.min(100, score));
  };

  const fetchPartnerData = async () => {
    if (!partnerId) return;

    try {
      setIsLoading(true);
      
      const { data: partnerData, error: partnerError } = await supabase
        .from('partners')
        .select('id, name, phone, email, location')
        .eq('id', partnerId)
        .single();
      
      if (partnerError) throw partnerError;
      setPartner(partnerData);
      
      const { data: leadsData, error: leadsError } = await supabase
        .from('leads')
        .select('*')
        .eq('partner_id', partnerId)
        .order('created_at', { ascending: false });
      
      if (leadsError) throw leadsError;
      
      const formattedLeads = leadsData.map((lead) => ({
        id: lead.id,
        name: lead.name,
        phone: lead.phone,
        address: lead.address,
        postalCode: lead.postal_code || "",
        service: lead.service as ServiceType,
        timeframe: lead.timeframe,
        stage: (lead.stage || 'Νέο') as LeadStageType,
        createdAt: lead.created_at,
        viewed: lead.link_opened || false
      }));
      
      setLeads(formattedLeads);
      
      const total = formattedLeads.length;
      const completed = formattedLeads.filter(lead => lead.stage === 'έκλεισεΗΔουλειά').length;
      const pending = formattedLeads.filter(lead => lead.stage === 'σεΕκκρεμότητα').length;
      const newLeads = formattedLeads.filter(lead => lead.stage === 'Νέο').length;
      const viewed = formattedLeads.filter(lead => lead.viewed).length;
      
      setStats({
        total,
        completed,
        pending,
        new: newLeads,
        viewed,
        successRate: total > 0 ? Math.round((completed / total) * 100) : 0
      });
      
    } catch (error) {
      console.error('Error fetching partner data:', error);
      toast({
        title: "Σφάλμα",
        description: "Δεν ήταν δυνατή η φόρτωση των δεδομένων.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const checkAuthorization = () => {
      const adminId = localStorage.getItem("adminId");
      const loggedInPartnerId = sessionStorage.getItem("partnerId");
      
      if (!adminId && (!loggedInPartnerId || loggedInPartnerId !== partnerId)) {
        navigate("/partner/login");
        return false;
      }
      
      return true;
    };

    if (!checkAuthorization()) return;
    fetchPartnerData();
  }, [partnerId, navigate]);

  useEffect(() => {
    if (!autoRefresh || !partnerId) return;

    const interval = window.setInterval(() => {
      fetchPartnerData();
    }, 30000);

    return () => window.clearInterval(interval);
  }, [autoRefresh, partnerId]);

  const refreshData = () => {
    fetchPartnerData();
  };

  const getServiceLabel = (service: string) => {
    switch (service) {
      case "εξοικονομώ": return "Εξοικονομώ";
      case "αντλίεςΘερμότητας": return "Αντλίες Θερμότητας";
      case "φωτοβολταϊκά": return "Φωτοβολταϊκά";
      case "κλιματιστικά": return "Κλιματιστικά";
      case "φυσικόΑέριο": return "Φυσικό Αέριο";
      case "ηλιακά": return "Ηλιακά";
      case "άλλο": return "Άλλο";
      default: return service;
    }
  };

  const getStageLabel = (stage: string) => {
    switch (stage) {
      case "σεΕκκρεμότητα": return "Σε Εκκρεμότητα";
      case "έκλεισεΗΔουλειά": return "Έκλεισε η Δουλειά";
      case "ήθελεΠληροφορίες": return "Ήθελε Πληροφορίες";
      case "Νέο": return "Νέο";
      default: return stage;
    }
  };

  const getStageColor = (stage: string) => {
    switch (stage) {
      case "σεΕκκρεμότητα": return "bg-yellow-100 text-yellow-800";
      case "έκλεισεΗΔουλειά": return "bg-green-100 text-green-800";
      case "ήθελεΠληροφορίες": return "bg-blue-100 text-blue-800";
      case "Νέο": return "bg-purple-100 text-purple-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };

  const pipelineCounts = useMemo(() => ({
    new: leads.filter(lead => lead.stage === "Νέο").length,
    pending: leads.filter(lead => lead.stage === "σεΕκκρεμότητα").length,
    info: leads.filter(lead => lead.stage === "ήθελεΠληροφορίες").length,
    closed: leads.filter(lead => lead.stage === "έκλεισεΗΔουλειά").length,
  }), [leads]);

  const filteredLeads = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return leads.filter((lead) => {
      const searchMatch =
        !normalizedSearch ||
        lead.name.toLowerCase().includes(normalizedSearch) ||
        lead.phone.includes(normalizedSearch) ||
        lead.address.toLowerCase().includes(normalizedSearch);

      const stageMatch = stageFilter === "all" || lead.stage === stageFilter;

      return searchMatch && stageMatch;
    });
  }, [leads, searchTerm, stageFilter]);

  const topPriorityLeads = useMemo(() => {
    return [...leads]
      .sort((a, b) => calculateLeadScore(b) - calculateLeadScore(a))
      .slice(0, 5);
  }, [leads]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Φόρτωση στοιχείων...</p>
        </div>
      </div>
    );
  }

  if (!partner) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <h1 className="text-2xl font-bold mb-4">Ο συνεργάτης δεν βρέθηκε</h1>
        <Button onClick={() => navigate("/partner/login")}>
          Επιστροφή στη σύνδεση
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PartnerDashboardHeader 
        partnerName={partner.name} 
        partnerId={partner.id}
        partnerPhone={partner.phone}
        partnerEmail={partner.email}
      />
      
      <div className="container mx-auto px-4 pb-8">
        <div className="bg-blue-50 border border-blue-200 rounded-lg mb-6 p-4">
          <div className="flex items-start space-x-3">
            <AlertCircle className="h-6 w-6 text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-blue-800 mb-1">Σημαντική υπενθύμιση</h3>
              <p className="text-blue-700 text-sm">Η άμεση επικοινωνία με τους υποψήφιους πελάτες αυξάνει σημαντικά τις πιθανότητες συνεργασίας. Προτείνουμε να επικοινωνείτε εντός 1 ώρας από την ανάθεση του Lead για βέλτιστα αποτελέσματα.</p>
            </div>
          </div>
        </div>
        
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">Πίνακας Ελέγχου</h2>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant={autoRefresh ? "default" : "outline"}
              onClick={() => setAutoRefresh(prev => !prev)}
            >
              Live {autoRefresh ? "ON" : "OFF"}
            </Button>
            <Button size="sm" variant="outline" onClick={refreshData} className="flex items-center gap-1">
              <RefreshCw className="h-4 w-4 mr-1" />
              Ανανέωση
            </Button>
          </div>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 flex flex-col items-center justify-center text-center">
              <User className="h-8 w-8 text-blue-500 mb-2" />
              <p className="text-2xl font-bold">{stats.total}</p>
              <p className="text-xs text-muted-foreground">Συνολικά Leads</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 flex flex-col items-center justify-center text-center">
              <CheckCircle className="h-8 w-8 text-green-500 mb-2" />
              <p className="text-2xl font-bold">{stats.completed}</p>
              <p className="text-xs text-muted-foreground">Ολοκληρωμένα</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 flex flex-col items-center justify-center text-center">
              <Clock className="h-8 w-8 text-yellow-500 mb-2" />
              <p className="text-2xl font-bold">{stats.pending}</p>
              <p className="text-xs text-muted-foreground">Σε Εκκρεμότητα</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 flex flex-col items-center justify-center text-center">
              <Eye className="h-8 w-8 text-purple-500 mb-2" />
              <p className="text-2xl font-bold">{stats.successRate}%</p>
              <p className="text-xs text-muted-foreground">Ποσοστό Επιτυχίας</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Card className="border-slate-200">
            <CardContent className="p-3 text-center">
              <p className="text-xs text-slate-500">Νέο</p>
              <p className="text-xl font-bold text-purple-700">{pipelineCounts.new}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-3 text-center">
              <p className="text-xs text-slate-500">Σε Εκκρεμότητα</p>
              <p className="text-xl font-bold text-amber-700">{pipelineCounts.pending}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-3 text-center">
              <p className="text-xs text-slate-500">Πληροφορίες</p>
              <p className="text-xl font-bold text-blue-700">{pipelineCounts.info}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-3 text-center">
              <p className="text-xs text-slate-500">Κλεισμένα</p>
              <p className="text-xl font-bold text-emerald-700">{pipelineCounts.closed}</p>
            </CardContent>
          </Card>
        </div>
        
        <Tabs defaultValue="recent" value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="mb-4">
            <TabsTrigger value="recent">Top Προτεραιότητα</TabsTrigger>
            <TabsTrigger value="all">Οι Πελάτες μου</TabsTrigger>
            <TabsTrigger value="pipeline">Pipeline</TabsTrigger>
          </TabsList>
          
          <TabsContent value="recent">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Πρόσφατες αναθέσεις</CardTitle>
              </CardHeader>
              <CardContent>
                {topPriorityLeads.length > 0 ? (
                  <div className="space-y-4">
                    {topPriorityLeads.map((lead) => (
                      <div key={lead.id} className="border rounded-lg p-4 bg-white">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-medium">{lead.name}</h3>
                              <LeadStatusReminderBadge 
                                lastUpdated={lead.createdAt}
                                stage={lead.stage}
                              />
                            </div>
                            <div className="flex items-center mt-1 text-sm text-gray-500">
                              <Phone className="h-3 w-3 mr-1" />
                              <span>{lead.phone}</span>
                            </div>
                            <p className="text-sm mt-2 text-gray-600">{lead.address}</p>
                          </div>
                          <div className="flex flex-col items-end">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStageColor(lead.stage)}`}>
                              {getStageLabel(lead.stage)}
                            </span>
                            <Badge variant="outline" className="mt-1 text-emerald-700 border-emerald-200 bg-emerald-50">
                              Score {calculateLeadScore(lead)}
                            </Badge>
                            <span className="text-xs text-gray-500 mt-1">
                              {format(new Date(lead.createdAt), 'dd/MM/yyyy', { locale: el })}
                            </span>
                          </div>
                        </div>
                        <div className="mt-3 flex justify-between items-center">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {getServiceLabel(lead.service)}
                          </span>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => navigate(`/lead/${lead.id}`)}
                          >
                            Προβολή
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground">Δεν υπάρχουν αναθέσεις ακόμα</p>
                  </div>
                )}
                
                {leads.length > 5 && (
                  <div className="mt-4 text-center">
                    <Button 
                      variant="outline" 
                      onClick={() => setActiveTab("all")}
                    >
                      Προβολή Όλων ({leads.length})
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="all">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Οι πελάτες μου</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mb-4 space-y-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Αναζήτηση πελάτη, τηλεφώνου, διεύθυνσης..."
                      className="pl-10 bg-slate-50 border-slate-200"
                    />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant={stageFilter === "all" ? "default" : "outline"} onClick={() => setStageFilter("all")}>Όλα</Button>
                    <Button size="sm" variant={stageFilter === "Νέο" ? "default" : "outline"} onClick={() => setStageFilter("Νέο")}>Νέα</Button>
                    <Button size="sm" variant={stageFilter === "σεΕκκρεμότητα" ? "default" : "outline"} onClick={() => setStageFilter("σεΕκκρεμότητα")}>Εκκρεμότητα</Button>
                    <Button size="sm" variant={stageFilter === "ήθελεΠληροφορίες" ? "default" : "outline"} onClick={() => setStageFilter("ήθελεΠληροφορίες")}>Πληροφορίες</Button>
                    <Button size="sm" variant={stageFilter === "έκλεισεΗΔουλειά" ? "default" : "outline"} onClick={() => setStageFilter("έκλεισεΗΔουλειά")}>Κλεισμένα</Button>
                  </div>
                </div>

                {filteredLeads.length > 0 ? (
                  <div className="space-y-4">
                    {filteredLeads.map((lead) => (
                      <div key={lead.id} className="border rounded-lg p-4 bg-white">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-medium">{lead.name}</h3>
                              <LeadStatusReminderBadge 
                                lastUpdated={lead.createdAt}
                                stage={lead.stage}
                              />
                            </div>
                            <div className="flex items-center mt-1 text-sm text-gray-500">
                              <Phone className="h-3 w-3 mr-1" />
                              <span>{lead.phone}</span>
                            </div>
                            <p className="text-sm mt-2 text-gray-600">{lead.address}</p>
                          </div>
                          <div className="flex flex-col items-end">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStageColor(lead.stage)}`}>
                              {getStageLabel(lead.stage)}
                            </span>
                            <Badge variant="outline" className="mt-1 text-emerald-700 border-emerald-200 bg-emerald-50">
                              Score {calculateLeadScore(lead)}
                            </Badge>
                            <span className="text-xs text-gray-500 mt-1">
                              {format(new Date(lead.createdAt), 'dd/MM/yyyy', { locale: el })}
                            </span>
                          </div>
                        </div>
                        <div className="mt-3 flex justify-between items-center">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {getServiceLabel(lead.service)}
                          </span>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => navigate(`/lead/${lead.id}`)}
                          >
                            Προβολή
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground">Δεν βρέθηκαν πελάτες για τα φίλτρα</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="pipeline">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              {[
                { key: "Νέο" as LeadStageType, title: "Νέο", color: "text-purple-700" },
                { key: "σεΕκκρεμότητα" as LeadStageType, title: "Σε Εκκρεμότητα", color: "text-amber-700" },
                { key: "ήθελεΠληροφορίες" as LeadStageType, title: "Πληροφορίες", color: "text-blue-700" },
                { key: "έκλεισεΗΔουλειά" as LeadStageType, title: "Έκλεισε", color: "text-emerald-700" },
              ].map((col) => {
                const colLeads = leads.filter(lead => lead.stage === col.key);
                return (
                  <Card key={col.key} className="border-slate-200">
                    <CardHeader className="pb-2">
                      <CardTitle className={`text-base ${col.color}`}>{col.title} ({colLeads.length})</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {colLeads.slice(0, 4).map((lead) => (
                        <div key={lead.id} className="rounded-md border border-slate-200 p-2.5 bg-white">
                          <p className="font-medium text-sm text-slate-800 truncate">{lead.name}</p>
                          <p className="text-xs text-slate-500 truncate">{lead.phone}</p>
                          <div className="mt-1 flex items-center justify-between">
                            <span className="text-xs text-slate-500">{getServiceLabel(lead.service)}</span>
                            <span className="text-xs font-semibold text-emerald-700">{calculateLeadScore(lead)}</span>
                          </div>
                        </div>
                      ))}
                      {colLeads.length === 0 && (
                        <p className="text-xs text-slate-400">Δεν υπάρχουν leads</p>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default PartnerDashboard;
